import type { SessionMessageInfo } from '@opencode/client/promise';

import type { CodePromptSubmission, CodeSessionTurn } from './types';

import { turnPromptAttachments } from './attachments';

import { citationPreview } from '@/lib/code-sessions/assistant-citations';

/**
 * A turn a person wrote, rather than one cubic sent for a check-in or another agent's message. A
 * prompt the transcript shows for a turn leaves both unset instead.
 */
export function isPersonTurn(
  turn: Partial<Pick<CodeSessionTurn, 'checkInReason' | 'messageFrom'>>,
): boolean {
  return typeof turn.checkInReason !== 'string' && typeof turn.messageFrom !== 'string';
}

export const CHECK_IN_TURN_LABEL = 'Scheduled check-in';

/** A saved turn's prompt as a person reads it: a check-in shows its reason, not the agent's instructions. */
export function turnDisplayText(
  turn: Pick<CodeSessionTurn, 'prompt' | 'authoredText' | 'checkInReason' | 'messageFrom'>,
): string {
  if (typeof turn.messageFrom === 'string') {
    // What another agent wrote, without the message body cubic wrapped it in.
    return `Message from ${turn.messageFrom} · ${turnPromptText(turn)}`;
  }
  return typeof turn.checkInReason === 'string'
    ? `${CHECK_IN_TURN_LABEL} · ${turn.checkInReason}`
    : personMessageText(turn);
}

/**
 * The text a person's message shows. The composer appends its context, such as file comments or
 * quoted text, after the person's words, so its prompt begins with them and shows whole. A
 * request cubic words for a button, such as a findings card's, does not, and shows the short text
 * the person would have typed instead.
 */
export function personMessageText({
  prompt,
  authoredText,
}: {
  prompt: string;
  authoredText?: string | null;
}): string {
  return authoredText && !prompt.startsWith(authoredText) ? authoredText : prompt;
}

/**
 * What a failed turn's Retry sends: its saved text, skills and files. A send its runtime never got
 * goes again under its own id, so a copy that reached the runtime late is the same message and
 * runs once; any other goes as a new turn.
 */
export function retryPromptOf(
  sessionId: string,
  turn: CodeSessionTurn,
): Omit<CodePromptSubmission, 'delivery'> {
  return {
    text: turn.prompt,
    skills: turn.skills ?? [],
    authoredText: turn.authoredText ?? undefined,
    attachments: turnPromptAttachments(sessionId, turn),
    ...(turn.unsent ? { turnId: turn.id } : {}),
  };
}

/** The prompt as its author wrote it, without generated file-comment context. */
export function turnPromptText(turn: Pick<CodeSessionTurn, 'prompt' | 'authoredText'>): string {
  return turn.authoredText ?? turn.prompt;
}

/** A prompt on one line, with any quoted assistant text summarized. */
export function promptPreview(text: string): string {
  return citationPreview(text).replace(/\s+/g, ' ').trim();
}

export function userMessageText(message: SessionMessageInfo): string {
  return message.type === 'user' ? message.text : '';
}
